import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { publicApi } from '../utils/api';
import { mergeThemeSettings } from '../lib/themePresets';

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('wowrolla-theme') || 'dark';
  });
  const [adminTheme, setAdminTheme] = useState(() => {
    return localStorage.getItem('wowrolla-admin-theme') || localStorage.getItem('wowrolla-theme') || 'dark';
  });
  const [siteSettings, setSiteSettings] = useState(null);
  const siteSettingsRef = useRef(null);

  const adjustColor = (hex, amount) => {
    const sanitized = (hex || '').replace('#', '');
    if (!/^[0-9a-fA-F]{6}$/.test(sanitized)) {
      return hex;
    }
    const num = parseInt(sanitized, 16);
    const clamp = (v) => Math.max(0, Math.min(255, v));
    const r = clamp((num >> 16) + amount);
    const g = clamp(((num >> 8) & 0x00ff) + amount);
    const b = clamp((num & 0x0000ff) + amount);
    return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`;
  };

  const toRgbTriplet = (hex, fallback = '231, 198, 92') => {
    const sanitized = String(hex || '').replace('#', '').trim();
    if (!/^[0-9a-fA-F]{6}$/.test(sanitized)) {
      return fallback;
    }
    const num = parseInt(sanitized, 16);
    return `${(num >> 16) & 255}, ${(num >> 8) & 255}, ${num & 255}`;
  };

  const applyThemeSettings = useCallback((settings, activeThemeName) => {
    if (!settings) {
      return;
    }

    const mergedSettings = mergeThemeSettings(settings);
    const activeThemeSettings = mergedSettings[activeThemeName === 'light' ? 'light_theme' : 'dark_theme'] || mergedSettings;
    const root = document.documentElement;
    const tokenMap = {
      '--accent-primary': activeThemeSettings.primary_color,
      '--accent-secondary': activeThemeSettings.secondary_color,
      '--accent-highlight': activeThemeSettings.accent_highlight,
      '--glow-1': activeThemeSettings.glow_1,
      '--glow-2': activeThemeSettings.glow_2,
      '--glow-3': activeThemeSettings.glow_3,
      '--glow-4': activeThemeSettings.glow_4,
      '--glow-5': activeThemeSettings.glow_5,
      '--glow-6': activeThemeSettings.glow_6,
      '--bg-base': activeThemeSettings.bg_base,
      '--bg-surface': activeThemeSettings.bg_surface,
      '--bg-elevated': activeThemeSettings.bg_elevated,
      '--dropdown-bg': activeThemeSettings.dropdown_bg,
      '--dropdown-text': activeThemeSettings.dropdown_text,
      '--dropdown-hover-bg': activeThemeSettings.dropdown_hover_bg,
      '--text-primary': activeThemeSettings.text_primary,
      '--text-secondary': activeThemeSettings.text_secondary,
      '--text-muted': activeThemeSettings.text_muted,
      '--border-subtle': activeThemeSettings.border_subtle,
      '--border-strong': activeThemeSettings.border_strong,
      '--radius': activeThemeSettings.button_radius,
      '--button-radius': activeThemeSettings.button_radius,
    };

    Object.entries(tokenMap).forEach(([token, value]) => {
      if (value) {
        root.style.setProperty(token, value);
      }
    });

    root.style.setProperty('--glow-1-rgb', toRgbTriplet(activeThemeSettings.glow_1, '255, 232, 107'));
    root.style.setProperty('--glow-2-rgb', toRgbTriplet(activeThemeSettings.glow_2, '126, 243, 190'));
    root.style.setProperty('--glow-3-rgb', toRgbTriplet(activeThemeSettings.glow_3, '123, 197, 255'));
    root.style.setProperty('--glow-4-rgb', toRgbTriplet(activeThemeSettings.glow_4, '255, 157, 87'));
    root.style.setProperty('--glow-5-rgb', toRgbTriplet(activeThemeSettings.glow_5, '211, 157, 255'));
    root.style.setProperty('--glow-6-rgb', toRgbTriplet(activeThemeSettings.glow_6, '255, 122, 162'));

    if (activeThemeSettings.primary_color) {
      root.style.setProperty('--accent-primary-hover', adjustColor(activeThemeSettings.primary_color, 22));
      root.style.setProperty('--scrollbar-thumb', `${activeThemeSettings.primary_color}66`);
      root.style.setProperty('--scrollbar-thumb-hover', `${activeThemeSettings.primary_color}88`);
      root.style.setProperty('--ring', activeThemeSettings.primary_color);
    }

    if (activeThemeSettings.bg_surface) {
      root.style.setProperty('--card-bg', activeThemeSettings.bg_surface);
    }
  }, []);

  const syncThemeForPath = useCallback((pathname = window.location.pathname, settingsOverride) => {
    const effectiveSettings = settingsOverride || siteSettingsRef.current;
    if (!effectiveSettings) {
      return;
    }

    const isAdminRoute = pathname.startsWith('/admin');
    applyThemeSettings(effectiveSettings, isAdminRoute ? adminTheme : theme);
  }, [adminTheme, applyThemeSettings, theme]);

  useEffect(() => {
    const loadSiteSettings = async () => {
      try {
        const cached = localStorage.getItem('wowrolla-site-settings');
        let cachedSettings = null;
        if (cached) {
          cachedSettings = mergeThemeSettings(JSON.parse(cached));
          siteSettingsRef.current = cachedSettings;
          setSiteSettings(cachedSettings);
          syncThemeForPath(window.location.pathname, cachedSettings);
        }

        const response = await publicApi.getSiteSettings();
        const apiPayload = response.data || {};
        const mergedApiPayload = mergeThemeSettings(apiPayload);
        const payload = {
          ...mergedApiPayload,
          dark_theme: apiPayload.dark_theme || cachedSettings?.dark_theme || mergedApiPayload.dark_theme,
          light_theme: apiPayload.light_theme || cachedSettings?.light_theme || mergedApiPayload.light_theme,
        };
        localStorage.setItem('wowrolla-site-settings', JSON.stringify(payload));
        siteSettingsRef.current = payload;
        setSiteSettings(payload);
        syncThemeForPath(window.location.pathname, payload);
      } catch (error) {
        console.warn('Theme settings could not be loaded from API:', error);
      }
    };

    loadSiteSettings();

    const onThemeSettingsUpdated = (event) => {
      const payload = event?.detail;
      if (!payload) {
        return;
      }
      const mergedPayload = mergeThemeSettings(payload);
      siteSettingsRef.current = mergedPayload;
      setSiteSettings(mergedPayload);
      localStorage.setItem('wowrolla-site-settings', JSON.stringify(mergedPayload));
      syncThemeForPath(window.location.pathname, mergedPayload);
    };

    window.addEventListener('wowrolla-theme-settings-updated', onThemeSettingsUpdated);
    return () => window.removeEventListener('wowrolla-theme-settings-updated', onThemeSettingsUpdated);
  }, [syncThemeForPath]);

  useEffect(() => {
    localStorage.setItem('wowrolla-theme', theme);
    localStorage.setItem('wowrolla-admin-theme', adminTheme);
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.setAttribute('data-admin-theme', adminTheme);
    if (theme === 'light') {
      document.documentElement.classList.add('light');
      document.documentElement.classList.remove('dark');
    } else {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
    }

    if (siteSettings) {
      syncThemeForPath(window.location.pathname, siteSettings);
    }
  }, [adminTheme, siteSettings, syncThemeForPath, theme]);

  const toggleTheme = () => setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  const toggleAdminTheme = () => setAdminTheme(prev => prev === 'dark' ? 'light' : 'dark');

  const getCategoryColor = (index, total = 13) => {
    const hue = (index * (360 / Math.max(total, 1))) % 360;
    if (theme === 'light') {
      return {
        bg: `hsl(${hue}, 65%, 92%)`,
        text: `hsl(${hue}, 55%, 35%)`,
        border: `hsl(${hue}, 50%, 82%)`,
        accent: `hsl(${hue}, 60%, 50%)`,
        bgHover: `hsl(${hue}, 65%, 87%)`,
      };
    }
    return {
      bg: `hsl(${hue}, 40%, 15%)`,
      text: `hsl(${hue}, 60%, 75%)`,
      border: `hsl(${hue}, 35%, 25%)`,
      accent: `hsl(${hue}, 55%, 65%)`,
      bgHover: `hsl(${hue}, 40%, 20%)`,
    };
  };

  return (
    <ThemeContext.Provider value={{ theme, adminTheme, toggleTheme, toggleAdminTheme, getCategoryColor, syncThemeForPath }}>
      {children}
    </ThemeContext.Provider>
  );
};

